// @ts-nocheck
/**
 * スキル実行モジュール
 * スキルの発動処理とクールダウン管理
 *
 * 新スキルシステム対応（v2.0）
 * - effects配列による複合効果
 * - SKILL_EFFECT_TYPES対応
 * - 後方互換性維持
 *
 * @module twinKey/core/battle/SkillExecutor
 */

import { calculateDamage, calculateAoeDamage, calculateHeal } from './DamageCalculator';
import { SKILL_MULTIPLIERS, SKILL_COOLDOWNS } from '../../data/constants';
import { SKILL_EFFECT_TYPES, STATUS_TYPES, BUFF_TYPES } from '../../data/skills';

// ============================================================
// スキル実行
// ============================================================

/**
 * スキルを実行
 * 新スキルシステムのeffects配列に対応
 *
 * @param {Object} user - スキル使用者
 * @param {Object} skill - 使用するスキル
 * @param {Object|Array} targets - ターゲット（単体または配列）
 * @param {Object} battleState - バトル状態
 * @returns {{ success: boolean, results: Array, logs: Array }}
 */
export function executeSkill(user, skill, targets, battleState) {
  // スキル使用可能チェック
  const validation = validateSkillUse(user, skill);
  if (!validation.valid) {
    return {
      success: false,
      results: [],
      logs: [{
        type: 'error',
        message: validation.reason,
      }],
    };
  }

  let results = [];
  let logs = [];

  // 新スキルシステム（effects配列）
  if (skill.effects && Array.isArray(skill.effects)) {
    const effectResults = executeSkillEffects(user, skill, targets, battleState);
    results = effectResults.results;
    logs = effectResults.logs;
  } else {
    // レガシー処理（後方互換性）
    const legacyResults = executeLegacySkill(user, skill, targets);
    results = legacyResults.results;
    logs = legacyResults.logs;
  }

  // クールダウン設定（新/旧両対応）
  let cooldown = skill.cooldown ?? skill.maxCd ?? getDefaultCooldown(skill);
  // 装備によるクールダウン短縮（cooldown_reduction）
  const cdReduce = user.equipmentMods?.cdReduce || 0;
  if (cdReduce > 0) {
    cooldown = Math.max(0, cooldown - cdReduce);
  }
  skill.currentCooldown = cooldown;
  skill.currentCd = cooldown; // 後方互換性

  return {
    success: true,
    results,
    logs,
  };
}

/**
 * 新スキルシステムのeffects配列を実行
 * @param {Object} user - 使用者
 * @param {Object} skill - スキル
 * @param {Object|Array} targets - ターゲット（敵）
 * @param {Object} battleState - バトル状態
 * @returns {{ results: Array, logs: Array }}
 */
function executeSkillEffects(user, skill, targets, battleState) {
  const results = [];
  const logs = [];
  const targetArray = Array.isArray(targets) ? targets : [targets];
  const enemies = targetArray.filter(t => t && t.isAlive);
  const allies = battleState?.allies || [];
  const allAllies = [user, ...allies.filter(a => a.id !== user.id && a.isAlive)];

  for (const effect of skill.effects) {
    switch (effect.type) {
      // ============================================================
      // ダメージ系
      // ============================================================
      case SKILL_EFFECT_TYPES.DAMAGE:
      case SKILL_EFFECT_TYPES.DAMAGE_SINGLE:
        // 単体ダメージ
        {
          const target = enemies[0];
          if (target) {
            const multiplier = effect.multiplier || 1.0;
            // 属性ボーナス
            let finalMultiplier = multiplier;
            if (effect.attributeBonus && effect.attributeBonus[target.attribute]) {
              finalMultiplier = effect.attributeBonus[target.attribute];
            }
            const result = calculateDamage(user, target, finalMultiplier);
            target.stats.currentHp = Math.max(0, target.stats.currentHp - result.damage);

            // ライフスティール（スキル定義 or 装備効果）
            const effectLifesteal = effect.lifesteal || 0;
            const equipLifesteal = result.lifesteal || 0;
            const totalLifesteal = effectLifesteal + equipLifesteal;
            if (totalLifesteal > 0 && result.damage > 0) {
              const healAmount = Math.floor(result.damage * totalLifesteal);
              if (healAmount > 0) {
                user.stats.currentHp = Math.min(user.stats.maxHp, user.stats.currentHp + healAmount);
                logs.push({ type: 'heal', message: `${user.name}はHPを${healAmount}吸収！` });
              }
            }

            // 装備による状態異常付与判定
            tryEquipmentStatusInflicts(user, target, logs, results);

            results.push({
              type: 'damage',
              targetId: target.id,
              damage: result.damage,
              isCritical: result.isCritical,
              effectiveness: result.effectiveness,
            });

            let message = `${user.name}の${skill.name}！${target.name}に${result.damage}ダメージ！`;
            if (result.isCritical) message += '（クリティカル！）';
            logs.push({ type: result.isCritical ? 'critical' : 'damage', message });
          }
        }
        break;

      case SKILL_EFFECT_TYPES.DAMAGE_AOE:
      case SKILL_EFFECT_TYPES.DAMAGE_ALL:
        // 全体ダメージ
        {
          const multiplier = effect.multiplier || 0.7;
          const damageResults = calculateAoeDamage(user, enemies, multiplier);

          let totalDamage = 0;
          for (const dmgResult of damageResults) {
            const target = enemies.find(t => t.id === dmgResult.targetId);
            if (target) {
              target.stats.currentHp = Math.max(0, target.stats.currentHp - dmgResult.damage);
              totalDamage += dmgResult.damage;

              // 装備による状態異常付与判定（各ターゲットに対して）
              tryEquipmentStatusInflicts(user, target, logs, results);

              results.push({
                type: 'damage',
                targetId: dmgResult.targetId,
                damage: dmgResult.damage,
                isCritical: dmgResult.isCritical,
                effectiveness: dmgResult.effectiveness,
              });
              logs.push({
                type: dmgResult.isCritical ? 'critical' : 'damage',
                message: `${target.name}に${dmgResult.damage}ダメージ！`,
              });
            }
          }

          // 装備によるライフスティール（全体ダメージ合計から）
          const equipLifesteal = user.equipmentMods?.lifesteal || 0;
          if (equipLifesteal > 0 && totalDamage > 0) {
            const healAmount = Math.floor(totalDamage * equipLifesteal);
            if (healAmount > 0) {
              user.stats.currentHp = Math.min(user.stats.maxHp, user.stats.currentHp + healAmount);
              logs.push({ type: 'heal', message: `${user.name}はHPを${healAmount}吸収！` });
            }
          }
        }
        break;

      case SKILL_EFFECT_TYPES.DAMAGE_RANDOM:
        // ランダム対象ダメージ
        {
          const hitCount = effect.targets || 3;
          const multiplier = effect.multiplier || 0.8;
          let totalDamage = 0;
          for (let i = 0; i < hitCount; i++) {
            const aliveEnemies = enemies.filter(e => e.stats.currentHp > 0);
            if (aliveEnemies.length === 0) break;
            const target = aliveEnemies[Math.floor(Math.random() * aliveEnemies.length)];
            const result = calculateDamage(user, target, multiplier);
            target.stats.currentHp = Math.max(0, target.stats.currentHp - result.damage);
            totalDamage += result.damage;

            // 装備による状態異常付与判定（各ヒットに対して）
            tryEquipmentStatusInflicts(user, target, logs, results);

            results.push({ type: 'damage', targetId: target.id, damage: result.damage, isCritical: result.isCritical });
            logs.push({ type: 'damage', message: `${target.name}に${result.damage}ダメージ！` });
          }

          // 装備によるライフスティール（全ヒット合計から）
          const equipLifesteal = user.equipmentMods?.lifesteal || 0;
          if (equipLifesteal > 0 && totalDamage > 0) {
            const healAmount = Math.floor(totalDamage * equipLifesteal);
            if (healAmount > 0) {
              user.stats.currentHp = Math.min(user.stats.maxHp, user.stats.currentHp + healAmount);
              logs.push({ type: 'heal', message: `${user.name}はHPを${healAmount}吸収！` });
            }
          }
        }
        break;

      case SKILL_EFFECT_TYPES.DAMAGE_MULTI_HIT:
        // 複数回攻撃
        {
          const hits = effect.hits || 2;
          const multiplier = effect.multiplier || 1.0;
          const target = enemies[0];
          if (target) {
            let totalDamage = 0;
            for (let i = 0; i < hits; i++) {
              if (target.stats.currentHp <= 0) break;
              const result = calculateDamage(user, target, multiplier);
              target.stats.currentHp = Math.max(0, target.stats.currentHp - result.damage);
              totalDamage += result.damage;

              // 装備による状態異常付与判定（各ヒットに対して）
              tryEquipmentStatusInflicts(user, target, logs, results);

              results.push({ type: 'damage', targetId: target.id, damage: result.damage, isCritical: result.isCritical });
            }
            logs.push({ type: 'damage', message: `${user.name}の${skill.name}！${target.name}に${hits}連撃で${totalDamage}ダメージ！` });

            // 装備によるライフスティール（全ヒット合計から）
            const equipLifesteal = user.equipmentMods?.lifesteal || 0;
            if (equipLifesteal > 0 && totalDamage > 0) {
              const healAmount = Math.floor(totalDamage * equipLifesteal);
              if (healAmount > 0) {
                user.stats.currentHp = Math.min(user.stats.maxHp, user.stats.currentHp + healAmount);
                logs.push({ type: 'heal', message: `${user.name}はHPを${healAmount}吸収！` });
              }
            }
          }
        }
        break;

      // ============================================================
      // 回復系
      // ============================================================
      case SKILL_EFFECT_TYPES.HEAL:
      case SKILL_EFFECT_TYPES.HEAL_SINGLE:
        // 単体回復（最もHPが低い味方）
        {
          const healTargets = allAllies.filter(a => a.isAlive);
          healTargets.sort((a, b) => (a.stats.currentHp / a.stats.maxHp) - (b.stats.currentHp / b.stats.maxHp));
          const healTarget = healTargets[0] || user;
          const healPercent = effect.healPercent || 0.4;
          // 装備による回復効果上昇（heal_boost）
          const healBoost = 1.0 + (user.equipmentMods?.healBoost || 0);
          const healAmount = Math.floor(healTarget.stats.maxHp * healPercent * healBoost);

          const prevHp = healTarget.stats.currentHp;
          healTarget.stats.currentHp = Math.min(healTarget.stats.maxHp, healTarget.stats.currentHp + healAmount);
          const actualHeal = healTarget.stats.currentHp - prevHp;

          results.push({ type: 'heal', targetId: healTarget.id, amount: actualHeal });
          logs.push({ type: 'heal', message: `${healTarget.name}のHPが${actualHeal}回復！` });
        }
        break;

      case SKILL_EFFECT_TYPES.HEAL_SELF:
        // 自己回復
        {
          const healPercent = effect.healPercent || 0.25;
          // 装備による回復効果上昇（heal_boost）
          const healBoost = 1.0 + (user.equipmentMods?.healBoost || 0);
          const healAmount = Math.floor(user.stats.maxHp * healPercent * healBoost);
          const prevHp = user.stats.currentHp;
          user.stats.currentHp = Math.min(user.stats.maxHp, user.stats.currentHp + healAmount);
          const actualHeal = user.stats.currentHp - prevHp;

          results.push({ type: 'heal', targetId: user.id, amount: actualHeal });
          logs.push({ type: 'heal', message: `${user.name}は自己回復！HPが${actualHeal}回復！` });
        }
        break;

      case SKILL_EFFECT_TYPES.HEAL_AOE:
      case SKILL_EFFECT_TYPES.HEAL_ALL:
        // 全体回復
        {
          const healPercent = effect.healPercent || 0.25;
          // 装備による回復効果上昇（heal_boost）
          const healBoost = 1.0 + (user.equipmentMods?.healBoost || 0);
          for (const healTarget of allAllies) {
            const healAmount = Math.floor(healTarget.stats.maxHp * healPercent * healBoost);
            const prevHp = healTarget.stats.currentHp;
            healTarget.stats.currentHp = Math.min(healTarget.stats.maxHp, healTarget.stats.currentHp + healAmount);
            const actualHeal = healTarget.stats.currentHp - prevHp;

            if (actualHeal > 0) {
              results.push({ type: 'heal', targetId: healTarget.id, amount: actualHeal });
              logs.push({ type: 'heal', message: `${healTarget.name}のHPが${actualHeal}回復！` });
            }
          }
        }
        break;

      case SKILL_EFFECT_TYPES.REVIVE:
        // 蘇生
        {
          // 装備による回復効果上昇（heal_boost）
          const healBoost = 1.0 + (user.equipmentMods?.healBoost || 0);
          const deadAllies = allies.filter(a => !a.isAlive || a.stats.currentHp <= 0);
          if (deadAllies.length > 0) {
            const reviveTarget = deadAllies[0];
            const revivePercent = effect.revivePercent || 0.3;
            reviveTarget.stats.currentHp = Math.floor(reviveTarget.stats.maxHp * revivePercent * healBoost);
            reviveTarget.isAlive = true;
            results.push({ type: 'revive', targetId: reviveTarget.id });
            logs.push({ type: 'heal', message: `${reviveTarget.name}が復活した！` });
          } else {
            // 生存時は大回復
            const aliveHealPercent = effect.aliveHealPercent || 0.7;
            const healTarget = allAllies.sort((a, b) => (a.stats.currentHp / a.stats.maxHp) - (b.stats.currentHp / b.stats.maxHp))[0];
            const healAmount = Math.floor(healTarget.stats.maxHp * aliveHealPercent * healBoost);
            healTarget.stats.currentHp = Math.min(healTarget.stats.maxHp, healTarget.stats.currentHp + healAmount);
            logs.push({ type: 'heal', message: `${healTarget.name}のHPが${healAmount}回復！` });
          }
        }
        break;

      // ============================================================
      // バフ系
      // ============================================================
      case SKILL_EFFECT_TYPES.BUFF:
      case SKILL_EFFECT_TYPES.BUFF_SINGLE:
        // 単体バフ（最もHPが低い味方 or 自身）
        {
          const buffTargets = allAllies.filter(a => a.isAlive);
          buffTargets.sort((a, b) => (a.stats.currentHp / a.stats.maxHp) - (b.stats.currentHp / b.stats.maxHp));
          const buffTarget = buffTargets[0] || user;
          const buffType = effect.buff || effect.buffType || BUFF_TYPES.ATK_UP;
          const duration = effect.duration || 3;
          const stacks = effect.stacks || 1;
          const value = effect.value || (stacks * 0.1);

          applyBuff(buffTarget, buffType, value, duration);
          results.push({ type: 'buff', targetId: buffTarget.id, buffType, value, duration });
          logs.push({ type: 'buff', message: `${buffTarget.name}に${getBuffName(buffType)}が付与された！` });
        }
        break;

      case SKILL_EFFECT_TYPES.BUFF_SELF:
        // 自己バフ
        {
          const buffType = effect.buff || effect.buffType || BUFF_TYPES.ATK_UP;
          const duration = effect.duration || 3;
          const stacks = effect.stacks || 1;
          const value = effect.value || (stacks * 0.1);

          applyBuff(user, buffType, value, duration);
          results.push({ type: 'buff', targetId: user.id, buffType, value, duration });
          logs.push({ type: 'buff', message: `${user.name}に${getBuffName(buffType)}が付与された！` });
        }
        break;

      case SKILL_EFFECT_TYPES.BUFF_ALL:
        // 全体バフ
        {
          const buffType = effect.buff || effect.buffType || BUFF_TYPES.ATK_UP;
          const duration = effect.duration || 3;
          const stacks = effect.stacks || 1;
          const value = effect.value || (stacks * 0.1);

          for (const buffTarget of allAllies) {
            applyBuff(buffTarget, buffType, value, duration);
            results.push({ type: 'buff', targetId: buffTarget.id, buffType, value, duration });
          }
          logs.push({ type: 'buff', message: `味方全体に${getBuffName(buffType)}が付与された！` });
        }
        break;

      // ============================================================
      // デバフ系
      // ============================================================
      case SKILL_EFFECT_TYPES.DEBUFF:
      case SKILL_EFFECT_TYPES.DEBUFF_SINGLE:
        // 単体デバフ
        {
          const target = enemies[0];
          if (target) {
            const debuffType = effect.debuff || effect.debuffType || BUFF_TYPES.ATK_DOWN;
            const duration = effect.duration || 3;
            const stacks = effect.stacks || 1;
            const value = effect.value || (stacks * 0.1);
            const chance = effect.chance || 1.0;

            if (Math.random() < chance) {
              applyDebuff(target, debuffType, value, duration);
              results.push({ type: 'debuff', targetId: target.id, debuffType, value, duration });
              logs.push({ type: 'debuff', message: `${target.name}に${getDebuffName(debuffType)}が付与された！` });
            }
          }
        }
        break;

      case SKILL_EFFECT_TYPES.DEBUFF_ALL:
        // 全体デバフ
        {
          const debuffType = effect.debuff || effect.debuffType || BUFF_TYPES.ATK_DOWN;
          const duration = effect.duration || 3;
          const stacks = effect.stacks || 1;
          const value = effect.value || (stacks * 0.1);
          const chance = effect.chance || 1.0;

          for (const target of enemies) {
            if (Math.random() < chance) {
              applyDebuff(target, debuffType, value, duration);
              results.push({ type: 'debuff', targetId: target.id, debuffType, value, duration });
            }
          }
          logs.push({ type: 'debuff', message: `敵全体に${getDebuffName(debuffType)}が付与された！` });
        }
        break;

      // ============================================================
      // 状態異常系
      // ============================================================
      case SKILL_EFFECT_TYPES.STATUS:
      case SKILL_EFFECT_TYPES.STATUS_INFLICT:
        // 状態異常付与
        {
          const statusType = effect.status || effect.statusType;
          const chance = effect.chance || 1.0;
          const duration = effect.duration || 2;
          const stacks = effect.stacks || 1;
          const targetScope = effect.target || 'single';

          const statusTargets = targetScope === 'all_enemies' ? enemies : [enemies[0]];
          for (const target of statusTargets) {
            if (target && Math.random() < chance) {
              applyStatus(target, statusType, duration, stacks);
              results.push({ type: 'status', targetId: target.id, statusType, duration, stacks });
              logs.push({ type: 'status', message: `${target.name}は${getStatusName(statusType)}状態になった！` });
            }
          }
        }
        break;

      case SKILL_EFFECT_TYPES.STATUS_CURE:
        // 状態異常解除
        {
          const count = effect.count || 1;
          const cureTargets = allAllies.filter(a => a.statusEffects && a.statusEffects.length > 0);
          for (const cureTarget of cureTargets) {
            const removed = cureTarget.statusEffects.splice(0, count);
            if (removed.length > 0) {
              results.push({ type: 'cure', targetId: cureTarget.id, removed });
              logs.push({ type: 'heal', message: `${cureTarget.name}の状態異常が解除された！` });
            }
          }
        }
        break;

      // ============================================================
      // 複合効果系
      // ============================================================
      case SKILL_EFFECT_TYPES.DAMAGE_AND_STATUS:
        // ダメージ + 状態異常
        {
          const target = enemies[0];
          if (target) {
            const multiplier = effect.multiplier || 1.0;
            // 属性ボーナス
            let finalMultiplier = multiplier;
            if (effect.attributeBonus && effect.attributeBonus[target.attribute]) {
              finalMultiplier = effect.attributeBonus[target.attribute];
            }
            const result = calculateDamage(user, target, finalMultiplier);
            target.stats.currentHp = Math.max(0, target.stats.currentHp - result.damage);

            // 装備によるライフスティール
            const equipLifesteal = result.lifesteal || 0;
            if (equipLifesteal > 0 && result.damage > 0) {
              const healAmount = Math.floor(result.damage * equipLifesteal);
              if (healAmount > 0) {
                user.stats.currentHp = Math.min(user.stats.maxHp, user.stats.currentHp + healAmount);
                logs.push({ type: 'heal', message: `${user.name}はHPを${healAmount}吸収！` });
              }
            }

            // 装備による状態異常付与判定
            tryEquipmentStatusInflicts(user, target, logs, results);

            results.push({ type: 'damage', targetId: target.id, damage: result.damage, isCritical: result.isCritical });
            let message = `${user.name}の${skill.name}！${target.name}に${result.damage}ダメージ！`;
            if (result.isCritical) message += '（クリティカル！）';
            logs.push({ type: result.isCritical ? 'critical' : 'damage', message });

            // スキル定義の状態異常判定
            const statusType = effect.status || effect.statusType;
            const chance = effect.chance || 0.2;
            const stacks = effect.stacks || 1;
            if (statusType && Math.random() < chance) {
              applyStatus(target, statusType, 2, stacks);
              results.push({ type: 'status', targetId: target.id, statusType });
              logs.push({ type: 'status', message: `${target.name}は${getStatusName(statusType)}状態になった！` });
            }
          }
        }
        break;

      case SKILL_EFFECT_TYPES.DAMAGE_AND_DEBUFF:
        // ダメージ + デバフ
        {
          const target = enemies[0];
          if (target) {
            const multiplier = effect.multiplier || 1.0;
            const result = calculateDamage(user, target, multiplier);
            target.stats.currentHp = Math.max(0, target.stats.currentHp - result.damage);

            // 装備によるライフスティール
            const equipLifesteal = result.lifesteal || 0;
            if (equipLifesteal > 0 && result.damage > 0) {
              const healAmount = Math.floor(result.damage * equipLifesteal);
              if (healAmount > 0) {
                user.stats.currentHp = Math.min(user.stats.maxHp, user.stats.currentHp + healAmount);
                logs.push({ type: 'heal', message: `${user.name}はHPを${healAmount}吸収！` });
              }
            }

            // 装備による状態異常付与判定
            tryEquipmentStatusInflicts(user, target, logs, results);

            results.push({ type: 'damage', targetId: target.id, damage: result.damage, isCritical: result.isCritical });
            logs.push({ type: 'damage', message: `${user.name}の${skill.name}！${target.name}に${result.damage}ダメージ！` });

            // デバフ付与
            const debuffType = effect.debuff || BUFF_TYPES.ATK_DOWN;
            const stacks = effect.stacks || 1;
            applyDebuff(target, debuffType, stacks * 0.1, 3);
            results.push({ type: 'debuff', targetId: target.id, debuffType });
            logs.push({ type: 'debuff', message: `${target.name}に${getDebuffName(debuffType)}が付与された！` });
          }
        }
        break;

      case SKILL_EFFECT_TYPES.HEAL_AND_BUFF:
        // 回復 + バフ
        {
          const healPercent = effect.healPercent || 0.3;
          // 装備による回復効果上昇（heal_boost）
          const healBoost = 1.0 + (user.equipmentMods?.healBoost || 0);
          const healAmount = Math.floor(user.stats.maxHp * healPercent * healBoost);
          user.stats.currentHp = Math.min(user.stats.maxHp, user.stats.currentHp + healAmount);
          logs.push({ type: 'heal', message: `${user.name}のHPが${healAmount}回復！` });

          const buffType = effect.buff || BUFF_TYPES.ATK_UP;
          applyBuff(user, buffType, 0.2, 3);
          logs.push({ type: 'buff', message: `${user.name}に${getBuffName(buffType)}が付与された！` });
        }
        break;

      default:
        console.warn(`Unknown skill effect type: ${effect.type}`);
    }
  }

  return { results, logs };
}

/**
 * レガシースキル処理（後方互換性）
 * @param {Object} user - 使用者
 * @param {Object} skill - スキル
 * @param {Object|Array} targets - ターゲット
 * @returns {{ results: Array, logs: Array }}
 */
function executeLegacySkill(user, skill, targets) {
  const results = [];
  const logs = [];

  // スロット番号からデフォルト倍率を取得
  const slot = skill.slotNumber || getSkillSlot(skill.id) || 1;
  const defaultMultipliers = {
    1: { single: 1.0, aoe: 0.6 },
    2: { single: 1.3, aoe: 0.8 },
    3: { single: 1.8, aoe: 1.2 },
  };
  const multiplierConfig = SKILL_MULTIPLIERS[skill.id] || defaultMultipliers[slot];

  const skillType = skill.type || 'single';
  const multiplier = skillType === 'aoe'
    ? multiplierConfig.aoe
    : multiplierConfig.single;

  if (skillType === 'aoe') {
    const targetArray = Array.isArray(targets) ? targets : [targets];
    const damageResults = calculateAoeDamage(user, targetArray, multiplier);

    let totalDamage = 0;
    for (const result of damageResults) {
      const target = targetArray.find(t => t.id === result.targetId);
      if (target) {
        target.stats.currentHp = Math.max(0, target.stats.currentHp - result.damage);
        totalDamage += result.damage;

        // 装備による状態異常付与判定（各ターゲットに対して）
        tryEquipmentStatusInflicts(user, target, logs, results);

        results.push({
          type: 'damage',
          targetId: result.targetId,
          damage: result.damage,
          isCritical: result.isCritical,
          effectiveness: result.effectiveness,
        });
        logs.push({
          type: result.isCritical ? 'critical' : 'damage',
          message: `${user.name}の${skill.name}！${target.name}に${result.damage}ダメージ！`,
          effectiveness: result.effectiveness,
        });
      }
    }

    // 装備によるライフスティール（全体ダメージ合計から）
    const equipLifesteal = user.equipmentMods?.lifesteal || 0;
    if (equipLifesteal > 0 && totalDamage > 0) {
      const healAmount = Math.floor(totalDamage * equipLifesteal);
      if (healAmount > 0) {
        user.stats.currentHp = Math.min(user.stats.maxHp, user.stats.currentHp + healAmount);
        logs.push({ type: 'heal', message: `${user.name}はHPを${healAmount}吸収！` });
      }
    }
  } else {
    const target = Array.isArray(targets) ? targets[0] : targets;
    if (target && target.isAlive) {
      const result = calculateDamage(user, target, multiplier);
      target.stats.currentHp = Math.max(0, target.stats.currentHp - result.damage);

      // 装備によるライフスティール
      const equipLifesteal = result.lifesteal || 0;
      if (equipLifesteal > 0 && result.damage > 0) {
        const healAmount = Math.floor(result.damage * equipLifesteal);
        if (healAmount > 0) {
          user.stats.currentHp = Math.min(user.stats.maxHp, user.stats.currentHp + healAmount);
          logs.push({ type: 'heal', message: `${user.name}はHPを${healAmount}吸収！` });
        }
      }

      // 装備による状態異常付与判定
      tryEquipmentStatusInflicts(user, target, logs, results);

      results.push({
        type: 'damage',
        targetId: target.id,
        damage: result.damage,
        isCritical: result.isCritical,
        effectiveness: result.effectiveness,
      });

      let message = `${user.name}の${skill.name}！${target.name}に${result.damage}ダメージ！`;
      if (result.isCritical) message += '（クリティカル！）';
      logs.push({ type: result.isCritical ? 'critical' : 'damage', message });
    }
  }

  return { results, logs };
}

/**
 * スキル使用可能かを検証
 * @param {Object} user - 使用者
 * @param {Object} skill - スキル
 * @returns {{ valid: boolean, reason?: string }}
 */
export function validateSkillUse(user, skill) {
  if (!user.isAlive) {
    return { valid: false, reason: '戦闘不能のため使用できません' };
  }

  if (!skill) {
    return { valid: false, reason: 'スキルが存在しません' };
  }

  if (!skill.unlocked) {
    return { valid: false, reason: 'スキルが解放されていません' };
  }

  // 新旧両対応
  const currentCd = skill.currentCooldown ?? skill.currentCd ?? 0;
  if (currentCd > 0) {
    return { valid: false, reason: `クールダウン中（残り${currentCd}ターン）` };
  }

  return { valid: true };
}

/**
 * 使用可能なスキル一覧を取得
 * @param {Object} unit - ユニット
 * @returns {Array} 使用可能なスキル配列
 */
export function getAvailableSkills(unit) {
  if (!unit.isAlive) return [];

  return unit.skills.filter(skill => {
    const currentCd = skill.currentCooldown ?? skill.currentCd ?? 0;
    return skill.unlocked && currentCd === 0;
  });
}

/**
 * 全スキルのクールダウンを1減少
 * @param {Object} unit - ユニット
 */
export function reduceAllCooldowns(unit) {
  for (const skill of unit.skills) {
    // 新旧両対応
    if (skill.currentCooldown > 0) {
      skill.currentCooldown--;
    }
    if (skill.currentCd > 0) {
      skill.currentCd--;
    }
  }
}

/**
 * スキルのクールダウンをリセット
 * @param {Object} unit - ユニット
 * @param {number|string} skillIdentifier - スキルID または スロット番号（省略で全スキル）
 */
export function resetCooldown(unit, skillIdentifier = null) {
  for (const skill of unit.skills) {
    const matches = skillIdentifier === null ||
      skill.id === skillIdentifier ||
      skill.slotNumber === skillIdentifier;

    if (matches) {
      skill.currentCooldown = 0;
      skill.currentCd = 0;
    }
  }
}

// ============================================================
// スキル情報
// ============================================================

/**
 * スキル情報を取得（UI表示用）
 * @param {Object} skill - スキル
 * @param {Object} user - 使用者
 * @returns {Object} スキル情報
 */
export function getSkillInfo(skill, user) {
  const slot = skill.slotNumber || getSkillSlot(skill.id) || 1;
  const skillType = skill.type || 'single';

  // 倍率計算（新システム優先）
  let multiplier = 1.0;
  if (skill.effects && skill.effects.length > 0) {
    const damageEffect = skill.effects.find(e =>
      e.type === SKILL_EFFECT_TYPES.DAMAGE || e.type === SKILL_EFFECT_TYPES.DAMAGE_AOE
    );
    multiplier = damageEffect?.multiplier || 1.0;
  } else {
    const defaultMultipliers = {
      1: { single: 1.0, aoe: 0.6 },
      2: { single: 1.3, aoe: 0.8 },
      3: { single: 1.8, aoe: 1.2 },
    };
    const config = SKILL_MULTIPLIERS[skill.id] || defaultMultipliers[slot];
    multiplier = skillType === 'aoe' ? config.aoe : config.single;
  }

  const estimatedDamage = Math.floor(user.stats.attack * multiplier);
  const currentCd = skill.currentCooldown ?? skill.currentCd ?? 0;
  const maxCd = skill.cooldown ?? skill.maxCd ?? getDefaultCooldown(skill);

  return {
    id: skill.id,
    slotNumber: slot,
    name: skill.name,
    description: skill.description || '',
    type: skillType,
    multiplier,
    cooldown: maxCd,
    currentCooldown: currentCd,
    unlocked: skill.unlocked,
    canUse: skill.unlocked && currentCd === 0 && user.isAlive,
    estimatedDamage,
    typeText: skillType === 'aoe' ? '全体' : '単体',
    cdText: currentCd > 0 ? `CD: ${currentCd}` : '使用可能',
    effects: skill.effects || [],
  };
}

/**
 * スキル名のデフォルトを取得
 * @param {number|string} identifier - スロット番号またはスキルID
 * @returns {string} デフォルト名
 */
export function getDefaultSkillName(identifier) {
  const slotDefaults = {
    1: '基本スキル',
    2: '中級スキル',
    3: '必殺技',
  };
  const idDefaults = {
    skill_1: '基本スキル',
    skill_2: '中級スキル',
    skill_3: '必殺技',
  };
  return slotDefaults[identifier] || idDefaults[identifier] || '不明なスキル';
}

/**
 * スキルIDからスロット番号を取得
 * @param {string} skillId - スキルID
 * @returns {number} スロット番号（1-3）
 */
export function getSkillSlot(skillId) {
  if (!skillId) return 0;

  // 新形式: slotNumber直接
  if (typeof skillId === 'number') return skillId;

  // 旧形式: skill_1, skill_2, skill_3
  const slots = {
    skill_1: 1,
    skill_2: 2,
    skill_3: 3,
  };
  return slots[skillId] || 0;
}

/**
 * デフォルトクールダウンを取得
 * @param {Object} skill - スキル
 * @returns {number} クールダウンターン数
 */
function getDefaultCooldown(skill) {
  const slot = skill.slotNumber || getSkillSlot(skill.id) || 1;
  const defaults = { 1: 2, 2: 3, 3: 4 };
  return SKILL_COOLDOWNS[skill.id] || defaults[slot] || 2;
}

// ============================================================
// 装備による状態異常付与
// ============================================================

/**
 * 装備効果による状態異常付与を判定・適用
 * @param {Object} attacker - 攻撃者
 * @param {Object} target - ターゲット
 * @param {Array} logs - ログ配列
 * @param {Array} results - 結果配列
 */
function tryEquipmentStatusInflicts(attacker, target, logs, results) {
  if (!attacker.equipmentMods || !target || !target.isAlive) return;

  const mods = attacker.equipmentMods;

  // 火傷付与 (burn_inflict)
  if (mods.burnInflict > 0 && Math.random() < mods.burnInflict) {
    applyStatus(target, STATUS_TYPES.BURN, 3, 1);
    results.push({ type: 'status', targetId: target.id, statusType: STATUS_TYPES.BURN });
    logs.push({ type: 'status', message: `${target.name}は火傷状態になった！` });
  }

  // 毒付与 (poison_inflict)
  if (mods.poisonInflict > 0 && Math.random() < mods.poisonInflict) {
    applyStatus(target, STATUS_TYPES.POISON, 3, 1);
    results.push({ type: 'status', targetId: target.id, statusType: STATUS_TYPES.POISON });
    logs.push({ type: 'status', message: `${target.name}は毒状態になった！` });
  }

  // 麻痺付与 (paralyze_inflict)
  if (mods.paralyzeInflict > 0 && Math.random() < mods.paralyzeInflict) {
    applyStatus(target, STATUS_TYPES.PARALYSIS, 2, 1);
    results.push({ type: 'status', targetId: target.id, statusType: STATUS_TYPES.PARALYSIS });
    logs.push({ type: 'status', message: `${target.name}は麻痺状態になった！` });
  }

  // スタン付与 (stun_chance)
  if (mods.stunChance > 0 && Math.random() < mods.stunChance) {
    applyStatus(target, STATUS_TYPES.STUN, 1, 1);
    results.push({ type: 'status', targetId: target.id, statusType: STATUS_TYPES.STUN });
    logs.push({ type: 'status', message: `${target.name}はスタン状態になった！` });
  }

  // 即死付与 (instant_death_inflict)
  if (mods.instantDeathInflict > 0 && Math.random() < mods.instantDeathInflict) {
    // 即死耐性チェック
    const resistChance = target.equipmentMods?.instantDeathResist || 0;
    if (Math.random() >= resistChance) {
      target.stats.currentHp = 0;
      target.isAlive = false;
      results.push({ type: 'instant_death', targetId: target.id });
      logs.push({ type: 'critical', message: `${target.name}は即死した！` });
    } else {
      logs.push({ type: 'info', message: `${target.name}は即死を耐えた！` });
    }
  }
}

// ============================================================
// バフ・デバフ・状態異常処理
// ============================================================

/**
 * バフを付与
 * @param {Object} target - 対象
 * @param {string} buffType - バフタイプ
 * @param {number} value - 効果値
 * @param {number} duration - 持続ターン
 */
function applyBuff(target, buffType, stacks, duration) {
  if (!target.buffs) target.buffs = [];

  // BuffManager/DamageCalculator互換フォーマット
  // stack: スタック数（BuffManager用）
  // value: 直接修正値（DamageCalculator用、1スタック=0.1）
  // remainingTurns: 残りターン数（BuffManager用）
  const existing = target.buffs.find(b => b.type === buffType);
  if (existing) {
    existing.stack = (existing.stack || 1) + stacks;
    existing.value = existing.stack * 0.1;  // 1スタック = 10%
    existing.remainingTurns = Math.max(existing.remainingTurns || 0, duration);
    existing.stacks = existing.stack;  // 後方互換
    existing.duration = existing.remainingTurns;  // 後方互換
  } else {
    target.buffs.push({
      type: buffType,
      stack: stacks,
      stacks: stacks,  // 後方互換
      value: stacks * 0.1,  // 1スタック = 10%
      remainingTurns: duration,
      duration: duration,  // 後方互換
    });
  }
}

/**
 * デバフを付与
 * @param {Object} target - 対象
 * @param {string} debuffType - デバフタイプ
 * @param {number} stacks - スタック数
 * @param {number} duration - 持続ターン
 */
function applyDebuff(target, debuffType, stacks, duration) {
  if (!target.debuffs) target.debuffs = [];

  // BuffManager/DamageCalculator互換フォーマット
  const existing = target.debuffs.find(d => d.type === debuffType);
  if (existing) {
    existing.stack = (existing.stack || 1) + stacks;
    existing.value = existing.stack * 0.1;  // 1スタック = 10%
    existing.remainingTurns = Math.max(existing.remainingTurns || 0, duration);
    existing.stacks = existing.stack;  // 後方互換
    existing.duration = existing.remainingTurns;  // 後方互換
  } else {
    target.debuffs.push({
      type: debuffType,
      stack: stacks,
      stacks: stacks,  // 後方互換
      value: stacks * 0.1,  // 1スタック = 10%
      remainingTurns: duration,
      duration: duration,  // 後方互換
    });
  }
}

/**
 * 状態異常を付与
 * @param {Object} target - 対象
 * @param {string} statusType - 状態異常タイプ
 * @param {number} duration - 持続ターン
 * @param {number} stacks - スタック数（デフォルト1）
 */
function applyStatus(target, statusType, duration, stacks = 1) {
  if (!target.statusEffects) target.statusEffects = [];

  // 状態異常はスタック加算
  // StatusEffectEngineと互換性のあるフィールド名を使用
  const existing = target.statusEffects.find(s => s.type === statusType);
  if (!existing) {
    target.statusEffects.push({
      type: statusType,
      remainingTurns: duration,  // StatusEffectEngine互換
      stack: stacks,             // StatusEffectEngine互換（単数形）
    });
  } else {
    existing.remainingTurns = Math.max(existing.remainingTurns || 0, duration);
    existing.stack = (existing.stack || 1) + stacks;
  }
}

/**
 * バフ名を取得
 */
function getBuffName(buffType) {
  const names = {
    [BUFF_TYPES.ATK_UP]: '攻撃力上昇',
    [BUFF_TYPES.DEF_UP]: '防御力上昇',
    [BUFF_TYPES.SPD_UP]: '速度上昇',
    [BUFF_TYPES.CRIT_UP]: 'クリティカル率上昇',
    [BUFF_TYPES.EVASION_UP]: '回避上昇',
    [BUFF_TYPES.REGEN]: '再生',
    [BUFF_TYPES.DAMAGE_REDUCE]: '被ダメージ軽減',
    [BUFF_TYPES.COUNTER]: '反撃',
    [BUFF_TYPES.INVINCIBLE]: '無敵',
    [BUFF_TYPES.TAUNT]: '挑発',
  };
  return names[buffType] || 'バフ';
}

/**
 * デバフ名を取得
 */
function getDebuffName(debuffType) {
  const names = {
    [BUFF_TYPES.ATK_DOWN]: '攻撃力低下',
    [BUFF_TYPES.DEF_DOWN]: '防御力低下',
    [BUFF_TYPES.SPD_DOWN]: '速度低下',
  };
  return names[debuffType] || 'デバフ';
}

/**
 * 状態異常名を取得
 */
function getStatusName(statusType) {
  const names = {
    [STATUS_TYPES.BURN]: '火傷',
    [STATUS_TYPES.FREEZE]: '凍結',
    [STATUS_TYPES.PARALYSIS]: '麻痺',
    [STATUS_TYPES.POISON]: '毒',
    [STATUS_TYPES.SLEEP]: '睡眠',
    [STATUS_TYPES.BLIND]: '暗闇',
    [STATUS_TYPES.SILENCE]: '沈黙',
    [STATUS_TYPES.STUN]: 'スタン',
    [STATUS_TYPES.CONFUSION]: '混乱',
    [STATUS_TYPES.CURSE]: '呪い',
    [STATUS_TYPES.WEAKNESS]: '衰弱',
    [STATUS_TYPES.BLEED]: '出血',
    [STATUS_TYPES.TIME_STOP]: '時止',
    [STATUS_TYPES.EXHAUST]: '虚脱',
  };
  return names[statusType] || '状態異常';
}

// ============================================================
// スキル発動時のエフェクト用データ
// ============================================================

/**
 * スキル発動時のエフェクト情報を取得
 * @param {Object} skill - スキル
 * @param {Object} user - 使用者
 * @returns {Object} エフェクト情報
 */
export function getSkillEffectData(skill, user) {
  const slot = skill.slotNumber || getSkillSlot(skill.id) || 1;

  // スキルレベルによるエフェクト強度
  const intensity = slot; // 1-3

  // 属性カラー
  const attributeColors = {
    '炎': '#ff6b6b',
    '水': '#6b9eff',
    '光': '#ffd700',
    '闇': '#9d4ce6',
    '原始': '#6bff6b',
    '未来': '#00ffff',
    'なし': '#888888',
  };

  return {
    name: skill.name,
    intensity,
    color: attributeColors[user.attribute] || '#888888',
    isUltimate: slot === 3,
    duration: 500 + intensity * 200, // ms
  };
}
